import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = {
  green: { dot: "bg-green-500", ring: "shadow-[0_0_0_2px_rgba(34,197,94,0.18)]", label: "All systems operational" },
  amber: { dot: "bg-amber-500", ring: "shadow-[0_0_0_2px_rgba(245,158,11,0.18)]", label: "Some integrations degraded" },
  red: { dot: "bg-red-500", ring: "shadow-[0_0_0_2px_rgba(239,68,68,0.20)]", label: "Core service down" },
  unknown: { dot: "bg-gray-300", ring: "", label: "Status unknown" },
};

const FIELD_LABEL = {
  mongo: "MongoDB",
  resend: "Email (Resend)",
  flutterwave: "Payments (Flutterwave)",
  llm_key: "LLM key",
};

/**
 * Tiny coloured pulse in the Header showing live deployment health. Polls
 * `/api/health` every 60 s. Green = everything wired, amber = Mongo OK but a
 * side-channel env var is missing, red = Mongo unreachable.
 *
 * On hover (or tap on mobile) it expands a small inline panel with the
 * per-dependency status. No modal, no route — meant to be glanceable.
 */
export default function HealthPulse() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  // Track the last `overall` we observed so we can fire a toast ONCE on
  // transition (green → amber/red, or red → green for the recovery message).
  // We don't fire on initial load — that's just normal page bootstrap.
  const lastOverall = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchHealth = async () => {
      try {
        const r = await axios.get(`${API}/health`);
        if (!alive) return;
        const next = r.data;
        const prev = lastOverall.current;
        if (prev && next.overall !== prev) {
          if (next.overall === "red") {
            toast.error("Core service down — see Health dot in header.", { duration: 8000 });
          } else if (next.overall === "amber") {
            toast.warning("System health degraded — see Health dot in header.", { duration: 6000 });
          } else if (next.overall === "green" && (prev === "amber" || prev === "red")) {
            toast.success("All systems back to operational.", { duration: 4000 });
          }
        }
        lastOverall.current = next.overall;
        setData(next);
      } catch {
        if (alive) setData({ overall: "red", mongo: "down", resend: "missing", flutterwave: "missing", llm_key: "missing" });
      }
    };
    fetchHealth();
    const t = setInterval(fetchHealth, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const tone = TONE[data?.overall] || TONE.unknown;

  return (
    <div className="relative" data-testid="health-pulse">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={tone.label}
        title={tone.label}
        data-testid="health-pulse-button"
      >
        <span className={`w-2.5 h-2.5 rounded-full ${tone.dot} ${tone.ring}`} data-testid={`health-dot-${data?.overall || "unknown"}`} />
      </button>
      {open && data && (
        <div
          className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-sm shadow-lg z-50 p-3"
          data-testid="health-pulse-panel"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">{tone.label}</p>
          <ul className="space-y-1.5">
            {Object.entries(FIELD_LABEL).map(([key, label]) => {
              const v = data[key];
              const ok = v === "ok";
              const isMongo = key === "mongo";
              const showLatency = isMongo && typeof data.mongo_ms === "number";
              return (
                <li key={key} className="flex items-center justify-between text-xs font-body">
                  <span className="text-gray-700">
                    {label}
                    {showLatency && (
                      <span className={`ml-1 text-[10px] ${data.mongo_ms > 500 ? "text-amber-700" : "text-gray-400"}`} data-testid="health-mongo-ms">
                        {data.mongo_ms}ms
                      </span>
                    )}
                  </span>
                  <span className={`font-mono text-[10px] rounded-sm px-1.5 py-0.5 ${ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`} data-testid={`health-${key}`}>
                    {v}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
